// Arduino Pro Mini en 3D (17.78 × 33.02 mm, mascarilla roja de SparkFun) con el extremo FTDI a la izquierda. Las
// posiciones de los componentes salen del archivo de placa de SparkFun (Arduino-Pro-Mini.brd, v14).
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape, glow, setGlow, emissiveMat, setEmissive } from '../kit.js';
import { PROMINI } from '../../parts/boards-mini.js';
import { getPins } from '../../parts/registry.js';

const W = PROMINI.W;
const D = PROMINI.D;
const TOP = PROMINI.PCB_TOP;
const RED = '#b0202b';

const silkCache = new Map();

function drawSilk(pins, version) {
  const { c, g } = canvas(W, D, 28);
  const X = (x) => x + W / 2;
  const Y = (z) => z + D / 2;
  g.fillStyle = RED;
  g.fillRect(0, 0, W, D);
  // Pistas de cobre bajo la máscara
  g.strokeStyle = 'rgba(80,8,14,0.5)';
  g.lineWidth = 0.2;
  for (let i = 0; i < 14; i++) {
    g.beginPath();
    g.moveTo(X(-11 + i * 1.7), Y(-5.4));
    g.lineTo(X(-10 + i * 1.7), Y(-3.2));
    g.lineTo(X(-10 + i * 1.7), Y(4.2));
    g.stroke();
  }
  const white = '#f6f2ec';
  for (const p of pins) {
    g.fillStyle = '#d8b465';
    g.beginPath();
    g.arc(X(p.x), Y(p.z), 0.95, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#121214';
    g.beginPath();
    g.arc(X(p.x), Y(p.z), 0.45, 0, Math.PI * 2);
    g.fill();
  }
  const short = { RAW: 'RAW', 'GND.1': 'GND', 'GND.2': 'GND', 'RESET.1': 'RST', 'RESET.2': 'RST', 'VCC.1': 'VCC', 1: 'TXO', 0: 'RXI' };
  for (const p of pins) {
    if (!p.insert) continue;
    const label = short[p.name] || p.name;
    // Rotulado hacia adentro de la placa, como la serigrafía de SparkFun.
    text(g, label, X(p.x), Y(p.z) + (p.z < 0 ? 2.0 : -2.0), { size: 0.95, color: white, align: p.z < 0 ? 'right' : 'left', rotate: -Math.PI / 2, weight: 700 });
  }
  for (const p of pins) {
    if (/^A[4-7]$/.test(p.name)) text(g, p.name, X(p.x), Y(p.z) + 1.5, { size: 0.8, color: white, weight: 700 });
  }
  text(g, 'BLK', X(-14.7), Y(7.0), { size: 0.75, color: white, weight: 700 });
  text(g, 'GRN', X(-14.7), Y(-7.0), { size: 0.75, color: white, weight: 700 });
  text(g, 'ARDUINO PRO MINI', X(-6.6), Y(-2.6), { size: 1.1, color: white, weight: 800 });
  text(g, version === '3v3' ? '3.3 V · 8 MHz' : '5 V · 16 MHz', X(-6.6), Y(2.9), { size: 1.0, color: white, weight: 700 });
  text(g, 'PWR', X(-5.4), Y(1.0), { size: 0.7, color: white, weight: 700 });
  text(g, 'STAT', X(14.8), Y(-2.4), { size: 0.75, color: white, weight: 700 });
  text(g, 'RESET', X(13.7), Y(2.0), { size: 0.75, color: white, weight: 700 });
  const tex = texture(c);
  tex.userData.shared = true;
  return tex;
}

function chipTop(label, w, d) {
  const { c, g } = canvas(w, d, 30);
  g.fillStyle = '#1b1b1e';
  g.fillRect(0, 0, w, d);
  g.fillStyle = '#34343a';
  g.beginPath();
  g.arc(1.4, 1.4, 0.5, 0, Math.PI * 2);
  g.fill();
  text(g, label, w / 2, d / 2 - 0.35, { size: 0.95, color: '#9a9ca1', weight: 600 });
  text(g, '328P-AU', w / 2, d / 2 + 0.75, { size: 0.8, color: '#7d7f84', weight: 500 });
  const t = texture(c);
  t.userData.shared = true;
  return t;
}

export function buildProMini(def, props) {
  const version = props.version === '3v3' ? '3v3' : '5v';
  const pins = getPins(def.type, props);
  if (!silkCache.has(version)) silkCache.set(version, drawSilk(pins, version));
  const silk = silkCache.get(version);
  const b = new Builder();
  const pcb = extrudeBoard(boardShape(W, D, { r: 0.5 }), PROMINI.PCB_T, silk, '#8a1820', { w: W, d: D, y0: PROMINI.PCB_Y });
  b.root.add(pcb);
  // Separadores de los encabezados de abajo y patas doradas
  for (const z of [-PROMINI.ROW, PROMINI.ROW]) b.box(12 * 2.54, 2.5, 2.54, M.blackPlastic, 1.27, 0.1, z);
  for (const p of pins.filter((q) => q.insert)) {
    b.box(0.64, TOP + 0.9, 0.64, M.gold, p.x, 0, p.z);
    b.cyl(0.85, 0.85, 0.3, M.tin, p.x, TOP, p.z, 10);
  }
  // Encabezado FTDI de 6 pines (macho recto)
  const ftdi = pins.filter((q) => !q.insert && q.x < -15);
  const zs = ftdi.map((q) => q.z);
  b.box(2.54, 2.5, (Math.max(...zs) - Math.min(...zs)) + 2.54, M.blackPlastic, -15.24, TOP, (Math.max(...zs) + Math.min(...zs)) / 2);
  for (const p of ftdi) b.box(0.64, PROMINI.HEADER_H - 0.4, 0.64, M.gold, p.x, TOP, p.z);
  // Almohadillas A4–A7
  for (const p of pins.filter((q) => /^A[4-7]$/.test(q.name))) {
    b.cyl(1.0, 1.0, 0.1, M.gold, p.x, TOP, p.z, 14);
    b.cyl(0.45, 0.45, 0.12, M.hole, p.x, TOP, p.z, 10);
  }
  // Componentes (posiciones del archivo de placa)
  const chipTex = chipTop('ATMEGA', 5.6, 5.6);
  b.box(7.0, 1.0, 7.0, M.chip, 2.54, TOP, 0, { ry: Math.PI / 4 });
  b.build();
  const label = new THREE.Mesh(new THREE.PlaneGeometry(5.0, 5.0), new THREE.MeshStandardMaterial({ map: chipTex, roughness: 0.5 }));
  label.rotation.x = -Math.PI / 2;
  label.rotation.z = Math.PI / 4;
  label.position.set(2.54, TOP + 1.01, 0);
  b.root.add(label);
  const small = new Builder();
  // Regulador MIC5205 (SOT-23-5), resonador cerámico, condensadores y resistencias
  small.box(3.0, 1.1, 1.6, M.chip, -7.67, TOP, 0);
  for (const dz of [-0.95, 0, 0.95]) small.box(0.5, 0.25, 0.7, M.tin, -8.9, TOP, dz);
  for (const dz of [-0.95, 0.95]) small.box(0.5, 0.25, 0.7, M.tin, -6.4, TOP, dz);
  small.box(3.2, 1.3, 1.3, mat('#c9b27a', { roughness: 0.6 }), -2.51, TOP, 4.65);
  small.box(3.2, 1.6, 1.6, mat('#c2993b', { roughness: 0.5 }), -9.52, TOP, 4.19);
  small.box(3.2, 1.6, 1.6, mat('#c2993b', { roughness: 0.5 }), -7.62, TOP, -4.32);
  for (const [x, z, rot] of [[-3.91, 1.7, 0], [9.27, -0.13, 0], [14.45, 4.34, 1], [-12.7, -3.91, 0], [-10.16, 0, 0], [11.28, 4.34, 1], [11.61, -4.83, 1]]) {
    small.box(rot ? 0.9 : 1.7, 0.5, rot ? 1.7 : 0.9, mat('#b9a98a', { roughness: 0.6 }), x, TOP, z);
  }
  small.build();
  b.root.add(small.root);
  // Botón RESET
  b.box(3.4, 1.6, 3.4, M.metal, 13.84, TOP, -0.13);
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 0.7, 16), mat('#2b2d31', { roughness: 0.5 }));
  cap.position.set(13.84, TOP + 1.95, -0.13);
  cap.userData.interactive = { kind: 'press', key: 'reset', label: 'RESET' };
  b.root.add(cap);
  // LED rojo de alimentación y LED verde STAT (pin 13)
  const leds = {};
  [['ON', -5.36, 0, '#ff3b2e', '#d8d2c8'], ['L', 14.78, -4.83, '#37ff5a', '#d8d2c8']].forEach(([name, x, z, g, base]) => {
    const m = emissiveMat(base, g);
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.6, 0.8), m);
    mesh.position.set(x, TOP + 0.3, z);
    b.root.add(mesh);
    const halo = glow(g, 6);
    halo.position.set(x, TOP + 1.2, z);
    b.root.add(halo);
    leds[name] = { m, halo, g, base };
  });
  const view = {
    root: b.root,
    update(v) {
      const set = (n, on) => {
        setEmissive(leds[n].m, on, 14);
        leds[n].m.color.set(on > 0.05 ? leds[n].g : leds[n].base);
        setGlow(leds[n].halo, on * 0.85);
      };
      set('L', v.L || 0);
      set('ON', 0.8 * (v.ON === true ? 1 : Number(v.ON) || 0));
    },
    reset() {
      view.update({});
    },
    press(key, down) {
      cap.position.y = TOP + (down ? 1.55 : 1.95);
    },
  };
  view.reset();
  return view;
}
